'use client';

import { useCart } from '@kidslabs/module-quote';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import { useCouponContext } from '../contexts/coupon.context';
import { useGetAppliedCoupons } from '../../../api';
import {
    type SalesRule,
    useAvailableCouponsQuery,
    useAvailableCartPriceRulesQuery,
} from '../../../types/front.graphql';
import { useValidateCoupons } from '../../../api/mutations';
import type { CouponSectionType } from '../components/coupon-list/coupon-list.types';
import { useTranslation } from 'next-i18next';
import { useLocalCouponEvaluation, type AvailableRuleData } from './use-local-coupon-evaluation';
import { useVoucherEvaluationContext } from './use-voucher-evaluation-context';
import type { MagentoCondition } from '../utils';

/**
 * Filter out null/undefined items from an array
 */
export const removeUnavailableItems = <T>(items: (T | null | undefined)[]): T[] => {
    return items.filter((item): item is T => !!item && item !== null);
};

/**
 * Recursively strip null items from nested conditions arrays
 * to convert GraphQL nullable condition trees into MagentoCondition trees.
 */
const sanitizeConditions = (
    conditions: ReadonlyArray<Record<string, unknown> | null | undefined> | null | undefined
): MagentoCondition[] | undefined => {
    if (!conditions) return undefined;
    return conditions
        .filter((c): c is Record<string, unknown> => c != null)
        .map(c => ({
            type: (c.type as string) ?? '',
            value: (c.value as string) ?? '',
            operator: c.operator as string | null | undefined,
            attribute: c.attribute as string | null | undefined,
            aggregator: c.aggregator as 'all' | 'any' | undefined,
            is_value_processed: c.is_value_processed as boolean | null | undefined,
            conditions: sanitizeConditions(
                c.conditions as ReadonlyArray<Record<string, unknown> | null | undefined> | null | undefined
            ),
        }));
};

/**
 * Coupon list hook with local-first evaluation.
 *
 * Flow:
 * 1. Fetch available coupons + available cart price rules (conditions + discount data)
 * 2. LOCAL (instant): Evaluate conditions + estimate discounts for each coupon
 * 3. Show local results immediately (valid/invalid split with estimated savings)
 * 4. SERVER (background): validateCoupons for usage limit checks
 * 5. Merge server results when they arrive (override local for usage-limited coupons)
 *
 * This eliminates the 300-800ms wait for server validation before showing the coupon list.
 */
export const useCouponList = () => {
    const { t } = useTranslation('flash-sale');
    const couponContext = useCouponContext();
    const { cartId } = useCart();
    // Track whether user has manually toggled selection to prevent
    // useEffect from overriding their deselection with the applied coupon.
    const userHasInteractedRef = useRef(false);
    const {
        loading: loadingAppliedCoupons,
        appliedCoupons,
        refetch: refetchAppliedCoupons,
    } = useGetAppliedCoupons({ cartId });

    // Fetch available coupons (list of coupon codes with name/description)
    const { data, loading: loadingAvailableCoupons } = useAvailableCouponsQuery({
        fetchPolicy: 'no-cache',
    });

    // Fetch available cart price rules (conditions + discount action data)
    const { data: rulesData, loading: loadingRules } = useAvailableCartPriceRulesQuery({
        fetchPolicy: 'cache-and-network',
    });

    // Server-side validation (runs in parallel for usage limit checks)
    const { validateCoupons, dataValidatedCoupons, loading: validatingCoupons, error } = useValidateCoupons();

    // Build evaluation context from current cart state
    const evaluationContext = useVoucherEvaluationContext();

    const selectedCouponCode = couponContext?.couponState$?.selectedCoupon?.get()?.code;

    const availableCoupons = useMemo(() => {
        if (!data?.availableCoupons) {
            return [];
        }
        return removeUnavailableItems(data?.availableCoupons);
    }, [data?.availableCoupons]);

    // Extract rule data for local evaluation
    const availableRules = useMemo((): AvailableRuleData[] => {
        if (!rulesData?.availableCartPriceRules?.items) {
            return [];
        }
        return removeUnavailableItems(rulesData.availableCartPriceRules.items).map(rule => ({
            rule_id: rule.rule_id,
            name: rule.name,
            simple_action: rule.simple_action,
            discount_amount: rule.discount_amount,
            discount_qty: rule.discount_qty,
            discount_step: rule.discount_step,
            max_discount_amount: rule.max_discount_amount,
            conditions: sanitizeConditions(rule.conditions),
        }));
    }, [rulesData?.availableCartPriceRules?.items]);

    // LOCAL-FIRST: Evaluate coupons instantly against cart conditions
    const { locallyValidCoupons, locallyInvalidCoupons } = useLocalCouponEvaluation(
        availableCoupons,
        availableRules,
        evaluationContext
    );

    // Merge local + server results. Server overrides local for usage limit cases.
    const { validCoupons, invalidCoupons } = useMemo(() => {
        // If server validation has completed, use server results as authority
        // but keep local evaluation for coupons the server didn't check
        if (dataValidatedCoupons && dataValidatedCoupons.length > 0) {
            const serverInvalidCodes = new Set(dataValidatedCoupons.filter(c => !c?.is_valid).map(c => c?.code));

            // Server says invalid (e.g., usage limit exceeded) → override local valid
            const valid = locallyValidCoupons
                .filter(ec => !serverInvalidCodes.has(ec.coupon.code))
                .map(ec => ec.coupon);

            // Combine: locally invalid + server-rejected coupons
            const invalid = [
                ...locallyInvalidCoupons.map(ec => ec.coupon),
                ...locallyValidCoupons.filter(ec => serverInvalidCodes.has(ec.coupon.code)).map(ec => ec.coupon),
            ];

            return {
                validCoupons: valid,
                invalidCoupons: invalid,
            };
        }

        // Server hasn't responded yet — use local results only (instant)
        return {
            validCoupons: locallyValidCoupons.map(ec => ec.coupon),
            invalidCoupons: locallyInvalidCoupons.map(ec => ec.coupon),
        };
    }, [dataValidatedCoupons, locallyValidCoupons, locallyInvalidCoupons]);

    const sortedCoupons = useMemo(() => {
        if (!validCoupons.length) {
            return validCoupons;
        }

        const priority = selectedCouponCode ?? appliedCoupons?.[0]?.code;
        if (!priority || validCoupons.length < 2 || validCoupons[0]?.code === priority) {
            return validCoupons;
        }

        const picked = validCoupons.find(c => c?.code === priority);
        if (!picked) {
            return validCoupons;
        }

        const rest = validCoupons.filter(c => c?.code !== priority);
        return [picked, ...rest];
    }, [validCoupons, selectedCouponCode, appliedCoupons]);

    const handleSelectCoupon = useCallback(
        async (coupon: SalesRule) => {
            userHasInteractedRef.current = true;
            const currentSelectedCode = couponContext?.couponState$?.selectedCoupon?.get()?.code;

            if (currentSelectedCode === coupon?.code) {
                couponContext?.couponState$?.selectedCoupon.set(undefined);
            } else {
                couponContext?.couponState$?.selectedCoupon.set(coupon);
            }
        },
        [couponContext]
    );

    const couponCodes = useMemo(
        () => availableCoupons?.map(coupon => coupon?.code).filter((code): code is string => !!code) ?? [],
        [availableCoupons]
    );

    const titleAvailableCoupons = useMemo(
        () =>
            t('text_coupon_list_avilable', 'Bạn có {{count}} mã giảm giá có thể sử dụng', {
                count: validCoupons?.length,
            }),
        [validCoupons?.length, t]
    );
    const titleUnavailableCoupons = useMemo(
        () =>
            t('text_coupon_unavailable', 'Bạn có {{count}} mã giảm giá chưa đủ điều kiện', {
                count: invalidCoupons?.length,
            }),
        [invalidCoupons?.length, t]
    );

    const couponSections: CouponSectionType[] = useMemo(
        () => [
            {
                isDisabled: false,
                title: titleAvailableCoupons,
                data: removeUnavailableItems(sortedCoupons) as CouponSectionType['data'],
            },
            {
                isDisabled: true,
                title: titleUnavailableCoupons,
                data: removeUnavailableItems(invalidCoupons) as CouponSectionType['data'],
            },
        ],
        [invalidCoupons, sortedCoupons, titleAvailableCoupons, titleUnavailableCoupons]
    );

    // Server validation runs in PARALLEL (background) — not blocking UI
    useEffect(() => {
        if (availableCoupons.length > 0 && cartId && couponCodes) {
            validateCoupons({
                variables: { input: { coupon_code: couponCodes, cart_id: cartId } },
            });
        }
    }, [cartId, couponCodes, validateCoupons, availableCoupons]);

    // Reset interaction flag when applied coupons change from server
    // (e.g., after successful apply/remove). This allows re-sync on next render.
    const prevAppliedCodeRef = useRef(appliedCoupons?.[0]?.code);
    useEffect(() => {
        const currentCode = appliedCoupons?.[0]?.code;
        if (prevAppliedCodeRef.current !== currentCode) {
            prevAppliedCodeRef.current = currentCode;
            userHasInteractedRef.current = false;
        }
    }, [appliedCoupons]);

    useEffect(() => {
        // Only auto-sync applied coupon to selection on initial load.
        // Skip if user has manually interacted (toggled/deselected).
        if (userHasInteractedRef.current) return;

        if (data && appliedCoupons?.length) {
            const matchedCoupon = availableCoupons.find(item => item?.code === appliedCoupons[0]?.code);

            if (matchedCoupon && selectedCouponCode !== matchedCoupon?.code) {
                couponContext?.couponState$?.selectedCoupon.set(matchedCoupon);
            }
        }
    }, [data, appliedCoupons, selectedCouponCode, couponContext, availableCoupons]);

    const handleRefresh = useCallback(() => {
        refetchAppliedCoupons();
    }, [refetchAppliedCoupons]);

    return {
        selectedCoupon: couponContext?.couponState$?.selectedCoupon.get(),
        onSelectCoupon: handleSelectCoupon,
        appliedCoupons,
        loadingAppliedCoupons,
        loadingAvailableCoupons: loadingAvailableCoupons || loadingRules,
        availableCoupon: data,
        couponSections,
        loading: validatingCoupons,
        error,
        onRefresh: handleRefresh,
        validCoupons,
        invalidCoupons,
        /** Local evaluation data for displaying estimated discounts in UI */
        locallyEvaluatedValid: locallyValidCoupons,
        locallyEvaluatedInvalid: locallyInvalidCoupons,
    };
};
